import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Filter store for job search filters
 * Matches Upwork's filter options
 */
const useFilterStore = create(
  persist(
    (set, get) => ({
      // Hydration tracking
      hasHydrated: false,
      setHasHydrated: (state) => set({ hasHydrated: state }),

      // Filter state
      filters: {
        category: '',
        experienceLevels: [], // ['entry', 'intermediate', 'expert']
        jobType: [], // ['hourly', 'fixed']
        proposalRanges: [], // ['0-5', '5-10', '10-15', '15-20', '20-50']
        clientInfo: [], // ['paymentVerified', 'previousClients']
        clientHistory: [], // ['noHires', '1-9', '10+']
        clientLocation: '',
        clientTimeZones: [],
        projectLength: [], // ['<1month', '1-3months', '3-6months', '6+months']
        budgetMin: '',
        budgetMax: '',
        datePosted: '', // '24h', '3d', '7d', '14d', '30d'
      },

      // Active filter count
      activeFilterCount: 0,

      // Actions
      setFilter: (filterName, value) => {
        set((state) => {
          const newFilters = {
            ...state.filters,
            [filterName]: value
          };
          return {
            filters: newFilters,
            activeFilterCount: calculateActiveFilterCount(newFilters)
          };
        });
      },

      toggleFilterValue: (filterName, value) => {
        set((state) => {
          const currentValues = state.filters[filterName] || [];
          let newValues;

          if (currentValues.includes(value)) {
            newValues = currentValues.filter(v => v !== value);
          } else {
            newValues = [...currentValues, value];
          }

          const newFilters = {
            ...state.filters,
            [filterName]: newValues
          };

          return {
            filters: newFilters,
            activeFilterCount: calculateActiveFilterCount(newFilters)
          };
        });
      },

      clearFilter: (filterName) => {
        set((state) => {
          const newFilters = {
            ...state.filters,
            [filterName]: Array.isArray(state.filters[filterName]) ? [] : ''
          };
          return {
            filters: newFilters,
            activeFilterCount: calculateActiveFilterCount(newFilters)
          };
        });
      },

      clearAllFilters: () => {
        set({
          filters: {
            category: '',
            experienceLevels: [],
            jobType: [],
            proposalRanges: [],
            clientInfo: [],
            clientHistory: [],
            clientLocation: '',
            clientTimeZones: [],
            projectLength: [],
            budgetMin: '',
            budgetMax: '',
            datePosted: '',
          },
          activeFilterCount: 0
        });
      },

      setAllFilters: (filters) => {
        set({
          filters,
          activeFilterCount: calculateActiveFilterCount(filters)
        });
      },

      // Get query params for API (returns plain object)
      getQueryParams: () => {
        const state = get();
        const params = {};

        if (state.filters.category) {
          params.category = state.filters.category;
        }
        if (state.filters.experienceLevels.length > 0) {
          params.experienceLevel = state.filters.experienceLevels.join(',');
        }
        if (state.filters.jobType.length > 0) {
          params.jobType = state.filters.jobType.join(',');
        }
        if (state.filters.proposalRanges.length > 0) {
          params.proposalRange = state.filters.proposalRanges.join(',');
        }
        if (state.filters.clientInfo.includes('paymentVerified')) {
          params.paymentVerified = 'true';
        }
        if (state.filters.clientInfo.includes('previousClients')) {
          params.previousClients = 'true';
        }
        if (state.filters.clientHistory.length > 0) {
          params.clientHistory = state.filters.clientHistory.join(',');
        }
        if (state.filters.clientLocation) {
          params.clientLocation = state.filters.clientLocation;
        }
        if (state.filters.clientTimeZones.length > 0) {
          params.clientTimeZones = state.filters.clientTimeZones.join(',');
        }
        if (state.filters.projectLength.length > 0) {
          params.projectLength = state.filters.projectLength.join(',');
        }
        if (state.filters.budgetMin) {
          params.budgetMin = state.filters.budgetMin;
        }
        if (state.filters.budgetMax) {
          params.budgetMax = state.filters.budgetMax;
        }

        return params;
      },

      // Build URLSearchParams for API requests
      buildQueryParams: () => {
        const state = get();
        const params = new URLSearchParams();

        if (state.filters.category) {
          params.append('category', state.filters.category);
        }
        if (state.filters.experienceLevels.length > 0) {
          params.append('experienceLevel', state.filters.experienceLevels.join(','));
        }
        if (state.filters.jobType.length > 0) {
          params.append('jobType', state.filters.jobType.join(','));
        }
        if (state.filters.proposalRanges.length > 0) {
          params.append('proposalRange', state.filters.proposalRanges.join(','));
        }
        if (state.filters.clientInfo.includes('paymentVerified')) {
          params.append('paymentVerified', 'true');
        }
        if (state.filters.clientInfo.includes('previousClients')) {
          params.append('previousClients', 'true');
        }
        if (state.filters.clientHistory.length > 0) {
          params.append('clientHistory', state.filters.clientHistory.join(','));
        }
        if (state.filters.clientLocation) {
          params.append('clientLocation', state.filters.clientLocation);
        }
        if (state.filters.clientTimeZones.length > 0) {
          params.append('clientTimeZones', state.filters.clientTimeZones.join(','));
        }
        if (state.filters.projectLength.length > 0) {
          params.append('projectLength', state.filters.projectLength.join(','));
        }
        if (state.filters.budgetMin) {
          params.append('budgetMin', state.filters.budgetMin);
        }
        if (state.filters.budgetMax) {
          params.append('budgetMax', state.filters.budgetMax);
        }
        if (state.filters.datePosted) {
          params.append('datePosted', state.filters.datePosted);
        }

        return params;
      }
    }),
    {
      name: 'job-filters',
      partialize: (state) => ({
        filters: state.filters,
        activeFilterCount: state.activeFilterCount
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    }
  )
);

// Helper to calculate active filter count
function calculateActiveFilterCount(filters) {
  let count = 0;

  if (filters.category) count++;
  if (filters.experienceLevels?.length > 0) count++;
  if (filters.jobType?.length > 0) count++;
  if (filters.proposalRanges?.length > 0) count++;
  if (filters.clientInfo?.length > 0) count++;
  if (filters.clientHistory?.length > 0) count++;
  if (filters.clientLocation) count++;
  if (filters.clientTimeZones?.length > 0) count++;
  if (filters.projectLength?.length > 0) count++;
  if (filters.budgetMin || filters.budgetMax) count++;
  if (filters.datePosted) count++;

  return count;
}

export default useFilterStore;
